import { Button, Card, Description, FieldError, Input, Label, Radio, RadioGroup, Spinner, Surface, TextArea, TextField } from "@heroui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { DeliveryType, OrderCreatedResponse, PublicProduct } from "../../../../shared/types";
import { MAX_CART_LINES, configurationIssue, useCart } from "../../../contexts/CartContext";
import type { CartLine } from "../../../contexts/CartContext";
import { useTenant } from "../../../contexts/TenantContext";
import { useToast } from "../../../contexts/ToastContext";
import { apiRequest, jsonBody } from "../../../lib/api";
import { formatLocal, formatUsd } from "../../../lib/currency";
import { currentPosition } from "../../../lib/geo";

import { BranchPicker, ConfigurationAlert, MenuSelect, QuantityField, ResponsiveDrawer } from "./MenuControls";
import { configurationSummary, ingredientPayload } from "./configurationSummary";

interface CheckoutStep {
  id: string;
  label: string;
}

const CHECKOUT_STEPS: CheckoutStep[] = [
  { id: "review", label: "Revisar" },
  { id: "branch", label: "Sucursal" },
  { id: "customer", label: "Datos" },
  { id: "fulfillment", label: "Entrega" },
  { id: "note", label: "Nota final" },
];

function CheckoutStepper({ currentStepIndex }: { currentStepIndex: number }) {
  return (
    <div className="grid grid-cols-5 gap-2" aria-label="Pasos del pedido">
      {CHECKOUT_STEPS.map((step, index) => (
        <div key={step.id} className="min-w-0">
          <div
            className={`h-1.5 rounded-full ${index <= currentStepIndex ? "bg-[var(--menu-accent)]" : "bg-border"}`}
          />
          <p
            className={`mt-2 truncate text-[11px] font-bold ${index === currentStepIndex ? "menu-text-primary" : "menu-text-muted"}`}
          >
            {step.label}
          </p>
        </div>
      ))}
    </div>
  );
}

function deliveryTypeLabel(type: DeliveryType): string {
  return { delivery: "Delivery", pickup: "Retiro", local: "En el local" }[type];
}

function deliveryAddressWithLocation(
  address: string,
  locationUrl: string,
): string {
  const cleanAddress = address.trim();
  if (!locationUrl) return cleanAddress;
  if (!cleanAddress) return locationUrl;
  if (cleanAddress.includes(locationUrl)) return cleanAddress;
  return `${cleanAddress}\nUbicación: ${locationUrl}`.slice(0, 500);
}

export function CartPanel({
  open,
  branchId,
  products,
  onClose,
  onBranch,
  onEdit,
}: {
  open: boolean;
  branchId: string;
  products: ReadonlyMap<string, PublicProduct>;
  onClose(): void;
  onBranch(id: string): void;
  onEdit(line: CartLine): void;
}) {
  const { tenant } = useTenant();
  const { lines, totalUsdCents, setQuantity, remove, clear } = useCart();
  const { show } = useToast();
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryType, setDeliveryType] = useState<DeliveryType>("pickup");
  const [address, setAddress] = useState("");
  const [sharedLocationUrl, setSharedLocationUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [checkoutStep, setCheckoutStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [checkoutIssue, setCheckoutIssue] = useState<string | null>(null);
  const idempotencyRef = useRef(crypto.randomUUID());

  const cartConfigurationIssue = useMemo(() => {
    if (!branchId) return "Selecciona la sucursal que recibirá el pedido.";
    for (const line of lines) {
      const issue = configurationIssue(
        line.product,
        line.configuration,
        products,
        branchId,
      );
      if (issue) return issue;
    }
    return null;
  }, [branchId, lines, products]);

  useEffect(() => {
    if (open) {
      setCheckoutStep(0);
      setCheckoutIssue(null);
    }
  }, [open]);

  useEffect(() => {
    if (!lines.length) setCheckoutStep(0);
  }, [lines.length]);

  if (!tenant) return null;

  const selectedBranch = tenant.branches.find(
    (branch) => branch.id === branchId,
  );

  const setIssue = (message: string, notify = true) => {
    setCheckoutIssue(message);
    if (notify) show(message, "error");
  };

  const validateCheckoutStep = (step: number, notify = true): boolean => {
    setCheckoutIssue(null);
    if (!lines.length) return false;
    if (step === 0) {
      if (cartConfigurationIssue) {
        setIssue(cartConfigurationIssue, notify);
        return false;
      }
      if (lines.length > MAX_CART_LINES) {
        setIssue(
          `El pedido admite como máximo ${MAX_CART_LINES} líneas.`,
          notify,
        );
        return false;
      }
    }
    if (step === 1 && !branchId) {
      setIssue("Selecciona la sucursal que recibirá el pedido.", notify);
      return false;
    }
    if (step === 2) {
      if (customerName.trim().length < 2) {
        setIssue("Escribe al menos 2 caracteres en el nombre.", notify);
        return false;
      }
      if (!/^\+?[0-9]{7,15}$/.test(customerPhone)) {
        setIssue("Usa un teléfono válido de 7 a 15 dígitos.", notify);
        return false;
      }
    }
    if (
      step === 3 &&
      deliveryType === "delivery" &&
      !address.trim() &&
      !sharedLocationUrl
    ) {
      setIssue("Indica dónde debemos entregar el pedido.", notify);
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateCheckoutStep(checkoutStep)) return;
    setCheckoutStep((step) => Math.min(CHECKOUT_STEPS.length - 1, step + 1));
  };

  const emptyCart = () => {
    clear();
    setCheckoutStep(0);
  };

  const shareCurrentLocation = async () => {
    setLocating(true);
    setCheckoutIssue(null);
    try {
      const position = await currentPosition();
      const link = `https://www.google.com/maps?q=${position.coords.latitude},${position.coords.longitude}`;
      setSharedLocationUrl(link);
      setAddress((current) => deliveryAddressWithLocation(current, link));
      show("Ubicación guardada para este pedido.", "success");
    } catch (caught) {
      const message =
        caught instanceof Error
          ? caught.message
          : "No se pudo obtener la ubicación.";
      setIssue(message);
    } finally {
      setLocating(false);
    }
  };

  const confirmOrder = async () => {
    setCheckoutIssue(null);
    if (checkoutStep !== CHECKOUT_STEPS.length - 1) return;
    if (!lines.length) return;
    for (let step = 0; step < CHECKOUT_STEPS.length; step += 1) {
      if (!validateCheckoutStep(step)) {
        setCheckoutStep(step);
        return;
      }
    }

    setSubmitting(true);
    try {
      const deliveryAddress =
        deliveryType !== "delivery"
          ? undefined
          : deliveryAddressWithLocation(address, sharedLocationUrl);
      const result = await apiRequest<OrderCreatedResponse>(
        "/api/public/orders",
        {
          method: "POST",
          auth: false,
          body: jsonBody({
            branchId,
            customerName,
            customerPhone,
            deliveryType,
            deliveryAddress,
            notes,
            idempotencyKey: idempotencyRef.current,
            items: lines.map((line) => ({
              ...line.configuration,
              quantity: line.quantity,
              ingredients: ingredientPayload(line.product, line.configuration),
            })),
          }),
        },
      );
      clear();
      setCheckoutStep(0);
      idempotencyRef.current = crypto.randomUUID();
      sessionStorage.setItem(
        `menucloud.order.whatsapp.${result.publicToken}`,
        result.whatsappUrl,
      );
      onClose();
      navigate(`/order/${result.publicToken}`, { state: result });
      show(
        `Pedido #${String(result.correlation).padStart(2, "0")} registrado.`,
        "success",
      );
    } catch (caught) {
      const message =
        caught instanceof Error
          ? caught.message
          : "No se pudo registrar el pedido.";
      setCheckoutIssue(message);
      show(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDrawer
      open={open}
      panel="cart"
      title="Tu pedido"
      wide
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      footer={
        lines.length ? (
          <div className="grid w-full gap-3">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="menu-text-muted text-xs font-semibold uppercase tracking-widest">
                  Total del pedido
                </p>
                <p className="menu-price text-sm font-bold">
                  {formatUsd(totalUsdCents)}
                </p>
              </div>
              <strong className="menu-price text-xl font-black">
                {formatLocal(
                  totalUsdCents,
                  tenant.exchangeRate,
                  tenant.localCurrency,
                  tenant.currencyDecimals,
                  tenant.currencySymbol,
                )}
              </strong>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                isDisabled={checkoutStep === 0 || submitting}
                size="lg"
                type="button"
                variant="secondary"
                onPress={() => setCheckoutStep((step) => Math.max(0, step - 1))}
              >
                Atrás
              </Button>
              {checkoutStep === CHECKOUT_STEPS.length - 1 ? (
                <Button
                  className="menu-critical-cta"
                  isPending={submitting}
                  size="lg"
                  type="button"
                  onPress={() => void confirmOrder()}
                >
                  {submitting && <Spinner color="current" size="sm" />}
                  Confirmar pedido
                </Button>
              ) : (
                <Button
                  className="menu-critical-cta"
                  size="lg"
                  type="button"
                  onPress={goNext}
                >
                  Continuar
                </Button>
              )}
            </div>
          </div>
        ) : undefined
      }
    >
      {!lines.length ? (
        <Surface
          className="menu-panel-alt grid min-h-72 place-items-center rounded-[var(--menu-radius)] border border-dashed border-border p-8 text-center"
          variant="secondary"
        >
          <div>
            <div className="mx-auto mb-4 grid size-14 place-items-center rounded-full bg-[var(--menu-accent)] text-2xl text-[var(--menu-accent-foreground)]">
              ○
            </div>
            <h3 className="menu-text-primary text-xl font-black">
              El pedido está vacío
            </h3>
            <p className="menu-text-muted mt-2 text-sm">
              Explora el menú y agrega algo que te provoque.
            </p>
            <Button className="mt-5" variant="secondary" onPress={onClose}>
              Volver al menú
            </Button>
          </div>
        </Surface>
      ) : (
        <div className="menu-density-section grid gap-8">
          <CheckoutStepper currentStepIndex={checkoutStep} />

          {(checkoutIssue ||
            (checkoutStep === 0 && cartConfigurationIssue)) && (
            <ConfigurationAlert
              issue={checkoutIssue ?? cartConfigurationIssue ?? ""}
            />
          )}

          {checkoutStep === 0 && (
            <section
              aria-labelledby="cart-lines-heading"
              className="grid gap-3"
            >
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                    Paso 1
                  </p>
                  <h3
                    id="cart-lines-heading"
                    className="menu-text-primary text-xl font-black"
                  >
                    Revisa tu pedido
                  </h3>
                  <p className="menu-text-muted mt-1 text-sm">
                    Verifica cantidades, opciones y subtotal antes de continuar.
                  </p>
                </div>
                <Button size="sm" variant="danger-soft" onPress={emptyCart}>
                  Vaciar
                </Button>
              </div>
              {lines.map((line) => {
                const details = configurationSummary(line, products);
                return (
                  <Card
                    key={line.id}
                    className="menu-product-card menu-panel border"
                    variant="default"
                  >
                    <Card.Header className="flex-row items-start justify-between gap-4">
                      <div>
                        <Card.Title className="menu-text-primary">
                          {line.product.name}
                        </Card.Title>
                        <Card.Description className="menu-text-secondary">
                          {details.length
                            ? details.join(" · ")
                            : "Preparación estándar"}
                        </Card.Description>
                      </div>
                      <strong className="menu-price shrink-0">
                        {formatUsd(line.unitPriceUsdCents * line.quantity)}
                      </strong>
                    </Card.Header>
                    <Card.Content>
                      <p className="menu-price text-xs">
                        {formatUsd(line.unitPriceUsdCents)} por unidad
                      </p>
                    </Card.Content>
                    <Card.Footer className="flex flex-wrap items-center justify-between gap-3">
                      <QuantityField
                        compact
                        label={`Cantidad de ${line.product.name}`}
                        value={line.quantity}
                        onChange={(quantity) => setQuantity(line.id, quantity)}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onPress={() => onEdit(line)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="danger-soft"
                          onPress={() => remove(line.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </Card.Footer>
                  </Card>
                );
              })}
            </section>
          )}

          {checkoutStep === 1 && (
            <Surface
              className="menu-panel-alt grid gap-5 rounded-[var(--menu-radius)] border border-border p-5 sm:p-6"
              variant="default"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                  Paso 2
                </p>
                <h3 className="menu-text-primary text-xl font-black">
                  Elige la sucursal
                </h3>
                <p className="menu-text-muted mt-1 text-sm">
                  Esta sucursal recibirá y preparará tu pedido.
                </p>
              </div>
              <BranchPicker
                compact
                branches={tenant.branches}
                selectedId={branchId}
                onSelect={onBranch}
              />
            </Surface>
          )}

          {checkoutStep === 2 && (
            <Surface
              className="menu-panel-alt grid gap-5 rounded-[var(--menu-radius)] border border-border p-5 sm:p-6"
              variant="default"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                  Paso 3
                </p>
                <h3 className="menu-text-primary text-xl font-black">
                  Datos del cliente
                </h3>
                <p className="menu-text-muted mt-1 text-sm">
                  Usaremos estos datos solo para coordinar este pedido.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  fullWidth
                  isRequired
                  minLength={2}
                  name="customerName"
                  value={customerName}
                  onChange={setCustomerName}
                >
                  <Label>Nombre</Label>
                  <Input
                    autoComplete="name"
                    placeholder="Tu nombre"
                    variant="secondary"
                  />
                  <FieldError>Escribe al menos 2 caracteres.</FieldError>
                </TextField>
                <TextField
                  fullWidth
                  isRequired
                  name="customerPhone"
                  type="tel"
                  value={customerPhone}
                  validate={(value) =>
                    /^\+?[0-9]{7,15}$/.test(value)
                      ? null
                      : "Usa entre 7 y 15 dígitos."
                  }
                  onChange={(value) =>
                    setCustomerPhone(value.replace(/[^+0-9]/g, ""))
                  }
                >
                  <Label>Teléfono</Label>
                  <Input
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+584121234567"
                    variant="secondary"
                  />
                  <Description>
                    Incluye el código de país cuando sea necesario.
                  </Description>
                  <FieldError />
                </TextField>
              </div>
            </Surface>
          )}

          {checkoutStep === 3 && (
            <Surface
              className="menu-panel-alt grid gap-5 rounded-[var(--menu-radius)] border border-border p-5 sm:p-6"
              variant="default"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                  Paso 4
                </p>
                <h3 className="menu-text-primary text-xl font-black">
                  Forma de recepción
                </h3>
                <p className="menu-text-muted mt-1 text-sm">
                  Elige cómo quieres recibir tu pedido.
                </p>
              </div>
              <RadioGroup
                name="deliveryType"
                value={deliveryType}
                variant="secondary"
                onChange={(value: string) =>
                  setDeliveryType(value as DeliveryType)
                }
              >
                <Label>¿Cómo recibirás el pedido?</Label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(
                    [
                      ["delivery", "Delivery"],
                      ["pickup", "Retiro"],
                      ["local", "En el local"],
                    ] as const
                  ).map(([value, label]) => (
                    <Radio
                      key={value}
                      className="menu-panel-alt rounded-[var(--menu-radius)] border border-border p-3"
                      value={value}
                    >
                      <Radio.Content>
                        <Radio.Control>
                          <Radio.Indicator />
                        </Radio.Control>
                        {label}
                      </Radio.Content>
                    </Radio>
                  ))}
                </div>
              </RadioGroup>

              {deliveryType === "delivery" && (
                <>
                  <TextField
                    fullWidth
                    isRequired
                    name="deliveryAddress"
                    value={address}
                    onChange={setAddress}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <Label>Dirección de entrega</Label>
                      <Button
                        isPending={locating}
                        size="sm"
                        type="button"
                        variant="secondary"
                        onPress={shareCurrentLocation}
                      >
                        {locating && <Spinner color="current" size="sm" />}
                        Compartir ubicación actual
                      </Button>
                    </div>
                    <TextArea
                      maxLength={500}
                      placeholder="Calle, edificio, piso, referencia o enlace de Google Maps"
                      rows={4}
                      variant="secondary"
                    />
                    <Description>
                      La ubicación se agrega automáticamente al confirmar el
                      pedido.
                    </Description>
                    <FieldError>
                      Indica dónde debemos entregar el pedido.
                    </FieldError>
                  </TextField>
                  {sharedLocationUrl ? (
                    <div className="rounded-[var(--menu-radius)] border border-success/30 bg-success/10 p-3 text-sm">
                      <p className="font-black text-success">
                        Ubicación guardada
                      </p>
                      <a
                        className="menu-text-primary mt-1 block break-all text-xs font-semibold underline"
                        href={sharedLocationUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {sharedLocationUrl}
                      </a>
                    </div>
                  ) : null}
                </>
              )}
            </Surface>
          )}

          {checkoutStep === 4 && (
            <Surface
              className="menu-panel-alt grid gap-5 rounded-[var(--menu-radius)] border border-border p-5 sm:p-6"
              variant="default"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--menu-accent)]">
                  Paso 5
                </p>
                <h3 className="menu-text-primary text-xl font-black">
                  Nota final y confirmación
                </h3>
                <p className="menu-text-muted mt-1 text-sm">
                  Registramos el pedido antes de abrir WhatsApp.
                </p>
              </div>
              <TextField
                fullWidth
                name="notes"
                value={notes}
                onChange={setNotes}
              >
                <Label>Notas</Label>
                <TextArea
                  maxLength={500}
                  placeholder="Alguna indicación útil para el equipo"
                  rows={3}
                  variant="secondary"
                />
                <Description>
                  Opcional. No agregues aquí ingredientes que no aparecen en el
                  menú.
                </Description>
                <FieldError />
              </TextField>
              <div className="menu-panel grid gap-3 rounded-[var(--menu-radius)] border border-border p-4">
                <div className="flex justify-between gap-4 text-sm">
                  <span className="menu-text-muted font-semibold">
                    Sucursal
                  </span>
                  <strong className="menu-text-primary text-right">
                    {selectedBranch?.name ?? "Sin seleccionar"}
                  </strong>
                </div>
                <div className="flex justify-between gap-4 text-sm">
                  <span className="menu-text-muted font-semibold">Cliente</span>
                  <strong className="menu-text-primary text-right">
                    {customerName || "Pendiente"}
                  </strong>
                </div>
                <div className="flex justify-between gap-4 text-sm">
                  <span className="menu-text-muted font-semibold">
                    Recepción
                  </span>
                  <strong className="menu-text-primary text-right">
                    {deliveryTypeLabel(deliveryType)}
                  </strong>
                </div>
                {deliveryType === "delivery" && (
                  <p className="menu-text-muted break-words text-xs">
                    {address || sharedLocationUrl || "Dirección pendiente"}
                  </p>
                )}
                {deliveryType === "delivery" &&
                sharedLocationUrl &&
                address &&
                address !== sharedLocationUrl ? (
                  <p className="menu-text-muted break-all text-xs">
                    Ubicación: {sharedLocationUrl}
                  </p>
                ) : null}
                <div className="flex justify-between gap-4 border-t border-border pt-3">
                  <span className="menu-text-primary font-black">Total</span>
                  <strong className="menu-price text-lg">
                    {formatLocal(
                      totalUsdCents,
                      tenant.exchangeRate,
                      tenant.localCurrency,
                      tenant.currencyDecimals,
                      tenant.currencySymbol,
                    )}
                  </strong>
                </div>
              </div>
            </Surface>
          )}
        </div>
      )}
    </ResponsiveDrawer>
  );
}
